/**
 * Cart Progress Bar - Dynamic updates for cart and mini cart
 * Handles progress calculation with per-country thresholds
 */

(function() {
  'use strict';

  var CartProgressBar = function(config) {
    this.config = {
      enabled: config.enabled || false,
      message: config.message || 'Spend {amount} more for free shipping!',
      successMessage: config.successMessage || '🎉 You\'ve qualified for free shipping!',
      color: config.color || '#4a4e54',
      freeShippingMap: config.freeShippingMap || {},
      currentCountry: config.currentCountry || ''
    };

    if (!this.config.currentCountry) {
      this.config.currentCountry = this.detectCountry();
    }

    if (!this.config.enabled) return;

    this.progressBars = document.querySelectorAll('[data-cart-progress-bar]');
    this.updateTimeout = null;
    this.originalFetch = window.fetch.bind(window);
    this.init();
  };

  CartProgressBar.prototype = {
    init: function() {
      if (this.progressBars.length === 0) return;
      this.observeCartChanges();
    },

    observeCartChanges: function() {
      var self = this;

      // Listen for cart update events
      document.addEventListener('cart:updated', function(event) {
        self.update(event);
      });

      document.addEventListener('cartUpdated', function() {
        self.fetchCartAndUpdate();
      });

      document.addEventListener('glbe.countryChanged', function(event) {
        if (event && event.detail && event.detail.countryCode) {
          self.config.currentCountry = event.detail.countryCode;
        } else if (typeof GLBE_PARAMS !== 'undefined' && GLBE_PARAMS.countryCode) {
          self.config.currentCountry = GLBE_PARAMS.countryCode;
        }
        self.fetchCartAndUpdate();
      });

      // Listen for AJAX cart changes via fetch wrapper
      var originalFetch = window.fetch;
      window.fetch = function() {
        var url = arguments[0];

        if (url && typeof url === 'object' && url.url) {
          url = url.url;
        } else if (url && typeof url !== 'string') {
          url = '';
        }

        return originalFetch.apply(this, arguments).then(function(response) {
          if (url && typeof url === 'string' && (
            url.includes('/cart/add') ||
            url.includes('/cart/update') ||
            url.includes('/cart/change') ||
            url.includes('/cart.js')
          )) {
            self.fetchCartAndUpdate();
          }
          return response;
        });
      };
    },

    fetchCartAndUpdate: function() {
      var self = this;

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      this.updateTimeout = setTimeout(function() {
        self.originalFetch('/cart.js')
          .then(function(response) { return response.json(); })
          .then(function(cart) {
            self.update(cart);
          })
          .catch(function(error) {
            console.error('Error fetching cart:', error);
          });
      }, 300);
    },

    update: function(cart) {
      var cartTotal = 0;

      if (cart && typeof cart === 'object') {
        if (cart.detail && cart.detail.cart && cart.detail.cart.total_price !== undefined) {
          cartTotal = cart.detail.cart.total_price;
        } else if (cart.detail && cart.detail.total_price !== undefined) {
          cartTotal = cart.detail.total_price;
        } else if (cart.total_price !== undefined) {
          cartTotal = cart.total_price;
        }
      }

      this.updateProgressBars(cartTotal);
    },

    updateProgressBars: function(cartTotal) {
      var self = this;

      this.progressBars.forEach(function(progressBar) {
        // Determine the list of countries this bar's metafield map supports
        var supportedAttr = progressBar.getAttribute('data-supported-countries');
        var hasMap = supportedAttr !== null;
        if (hasMap) {
          var supportedList = supportedAttr.split(',').map(function(c) { return c.trim().toUpperCase(); }).filter(Boolean);
          var current = (self.config.currentCountry || '').toUpperCase();
          if (supportedList.length > 0 && supportedList.indexOf(current) === -1) {
            progressBar.style.display = 'none';
            return;
          }
          progressBar.style.display = '';
        }

        var threshold = self.getThresholdForCountry(self.config.currentCountry);
        if (!threshold || threshold <= 0) {
          threshold = parseInt(progressBar.dataset.threshold);
        }
        if (!threshold || threshold <= 0) {
          threshold = 10000;
        }

        var remaining = threshold - cartTotal;
        var percentage = 0;

        if (threshold > 0 && cartTotal >= 0) {
          percentage = Math.min(100, Math.max(0, Math.round((cartTotal / threshold) * 100)));
        }
        if (isNaN(percentage)) {
          percentage = 0;
        }

        // Update progress fill
        var progressFill = progressBar.querySelector('[data-progress-fill]');
        if (progressFill) {
          progressFill.style.width = percentage + '%';
          progressFill.style.backgroundColor = self.config.color;
          progressFill.setAttribute('aria-valuenow', percentage);
        }

        // Update message
        var messageElement = progressBar.querySelector('[data-progress-message]');
        if (messageElement) {
          if (cartTotal >= threshold) {
            messageElement.innerHTML = '<span class="cart-progress-bar__success">' +
              self.config.successMessage + '</span>';
          } else {
            var remainingFormatted = self.formatMoney(remaining);
            var message = self.config.message.replace('{amount}', remainingFormatted);
            messageElement.innerHTML = '<span class="cart-progress-bar__text">' +
              message + '</span>';
          }
        }
      });
    },

    detectCountry: function() {
      if (typeof GLBE_PARAMS !== 'undefined' && GLBE_PARAMS.countryCode) {
        return GLBE_PARAMS.countryCode;
      }
      try {
        var stored = localStorage.getItem('nb__region_code');
        if (stored && stored.length === 2) return stored;
      } catch (e) {}
      return 'US';
    },

    getThresholdForCountry: function(countryCode) {
      var map = this.config.freeShippingMap;
      if (map && countryCode && map[countryCode] && map[countryCode].threshold !== undefined) {
        return map[countryCode].threshold; // already in cents
      }
      return 0;
    },

    formatMoney: function(cents) {
      // Use the same theme.Currency.formatMoney that the cart uses
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        var format = (theme.settings && theme.settings.moneyFormat) ? theme.settings.moneyFormat : '${{amount}}';
        return theme.Currency.formatMoney(cents, format);
      }

      // Fallback
      var moneyFormat = '${{amount}}';
      if (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyFormat) {
        moneyFormat = theme.settings.moneyFormat;
      }

      var dollars = (cents / 100).toFixed(2);
      return moneyFormat.replace('{{amount}}', dollars).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }
  };

  // Initialize on DOM ready (singleton pattern)
  var progressBarInstance = null;

  function initProgressBar() {
    if (typeof theme === 'undefined') {
      theme = {};
    }

    if (typeof theme.settings === 'undefined') {
      theme.settings = {};
    }

    if (theme.settings.cartProgressEnable && !progressBarInstance) {
      progressBarInstance = new CartProgressBar({
        enabled: theme.settings.cartProgressEnable,
        message: theme.settings.cartProgressMessage,
        successMessage: theme.settings.cartProgressSuccessMessage,
        color: theme.settings.cartProgressColor,
        freeShippingMap: theme.settings.cartProgressFreeShippingMap || {},
        currentCountry: theme.settings.cartProgressCurrentCountry || ''
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProgressBar);
  } else {
    initProgressBar();
  }

})();
